import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { TDSDetailAction } from './APIs/action';
import IconReusable from '../../../components/IconReusable';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import TDSTable from './Components/TDSTable';
import NoAccess from '../../../v3/Common/NoAccess/NoAccess';
import { CustomSelect } from '../../MUI_custom_component';

const CumulativeTDS = () => {
  const [panNo, setPanNo] = useState('');
  const [firms, setFirms] = useState([]);
  const [TDSData, setTDSData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [selectedYear, setSelectedYear] = useState(
    TDSData[0]?.financial_year || '',
  );

  // Get manufacturer and entity from Redux via Entity component
  const {
    selectedSupplierData,
    selectedSupplier: selectedManufacturer,
    selectedEntity: selectedCompanyEntity,
    selectedEntityInfo,
  } = useSelector(
    (state) => state.entityReducer
  );
  const hasAccess = selectedEntityInfo?.has_access;
  useEffect(() => {
    if (hasAccess !== false) {
      getTDSDetail();
    } else {
      setPanNo('');
      setFirms([]);
      setTDSData([]);
    }
  }, [selectedCompanyEntity, hasAccess]);

  const selectedData = TDSData?.find((d) => d?.financial_year === selectedYear);

  const getTDSDetail = async () => {
    setLoader(true);
    const { data, message, error } = await TDSDetailAction();
    setLoader(false);
    if (!error) {
      setPanNo(data?.pan_no || '');
      setFirms(data?.firms || []);
      setTDSData(data?.tds_info || []);
      setSelectedYear(data?.tds_info?.[0]?.financial_year || '');
    } else {
      toast.error(message || 'Failed to fetch TDS details');
    }
  };
  return (
    <>
      {hasAccess === false ?(
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh',
          }}
        >
          <NoAccess />
        </Box>
      ) : (
        <>
          <Box sx={{ mt: {xs : 0, md: 0 },p: { xs: 2, md: 2 }, pr: {xs: 0, md: 2},  display: 'flex', flexDirection: 'column', gap: { xs:1, md: 2 } }}>
            {firms && firms.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflowX: {
                    xs: firms.length > 1 ? 'auto' : 'visible',
                    md: firms.length > 2 ? 'auto' : 'visible',
                  },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                {firms.map((firm, index) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: '16px',
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      flex: {
                        xs: firms.length > 1 ? '0 0 92%' : '1 1 0',
                        md: firms.length > 2 ? '0 0 33%' : '1 1 0',
                      },
                      minWidth: {
                        xs: firms.length > 1 ? '92%' : 'auto',
                        md: firms.length > 2 ? '33%' : '300px',
                      },
                    }}
                  >
                    {firm.map((field, fieldIndex) => (
                      <Box
                        key={fieldIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography variant='labelD' sx={{ color: '#666' }}>
                          {field?.display_name}
                        </Typography>
                        <Typography
                          variant='body1'
                          sx={{ color: '#333', textAlign: 'right' }}
                        >
                          {field?.display_value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box sx={{ mt: {xs : 0, md: 0 }, p: 2, display: 'flex', flexDirection: 'column', gap: { xs:1, md:2 } }}>
            {TDSData && TDSData.length > 0 && (
              <>
              <CustomSelect
                label='Financial Year'
                size='small'
                width='100%'
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {TDSData.map((fy) => (
                  <MenuItem
                    key={fy?.financial_year}
                    value={fy?.financial_year}
                  >
                    {fy?.financial_year}
                  </MenuItem>
                ))}
              </CustomSelect>
              </>
            )}
            {selectedData && (
              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      boxShadow: 'none',
                      backgroundColor: '#FFFFFF',
                      p: 2,
                    }}
                  >
                    {selectedData?.summary_cards?.map((card, index) => {
                      if (card?.type === 'accordion') {
                        return (
                          <Accordion
                            key={index}
                            elevation={0}
                            expanded={expandedCard === index}
                            onChange={() => setExpandedCard(expandedCard === index ? null : index)}
                            sx={{
                              boxShadow: 'none',
                              '&:before': { display: 'none' },
                              '&.Mui-expanded': { margin: 0 },
                            }}
                          >
                            <AccordionSummary
                              sx={{
                                px: 0,
                                minHeight: 0,
                                m: 0,
                                borderBottom: expandedCard === index ? '1px dashed #E5E5E5' : 'none',
                                pb: expandedCard === index ? 1 : 0,
                                '&.Mui-expanded': {
                                  minHeight: 0,
                                },
                                '& .MuiAccordionSummary-content': {
                                  margin: 0,
                                },
                                '& .MuiAccordionSummary-content.Mui-expanded': {
                                  margin: 0,
                                },
                                '& .MuiAccordionSummary-expandIconWrapper': {
                                  display: 'none',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant='labelD' sx={{ color: '#636363' }}>
                                    {card?.label}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      transition: 'transform 0.2s',
                                      transform: expandedCard === index ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                  >
                                    <IconReusable name='KeyboardArrowDown' width={24} height={24} color='#666' />
                                  </Box>
                                </Box>
                                <Typography variant='body1' sx={{ fontWeight: 600, color: '#CC0000' }}>
                                  {card?.value}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 0, display: 'flex', flexDirection: 'column', gap: 1, pb: 0, pt: 1 }}>
                              {card?.items?.map((item, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Typography variant='labelD' sx={{ color: '#636363' }}>
                                    {item?.label}
                                  </Typography>
                                  <Typography
                                    variant='body1'
                                    sx={{
                                      fontWeight: 600,
                                      color:'#CC0000',
                                    }}
                                  >
                                    {item?.value}
                                  </Typography>
                                </Box>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        );
                      }

                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: '8px',
                            mb: '4px',
                            borderTop: card?.type === 'divider' ? '1px solid #D9D9D9' : 'none',
                          }}
                        >
                          <Typography
                            variant='labelD'
                            color='textSecondary'
                            sx={{ color: card?.type === 'divider' ? '#333333' : '#636363' }}
                          >
                            {card?.label || ''}
                          </Typography>
                          <Typography
                            variant='body1'
                            sx={{
                              fontWeight: 600,
                              color:
                                card?.type === 'fail'
                                  ? '#CC0000'
                                  : card?.type === 'success'
                                  ? '#008000'
                                  : card?.type === 'divider'
                                  ? '#333333'
                                  : '#666666',
                            }}
                          >
                            {card?.value || ''}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Paper>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 2
                  }}
                >
                  <Accordion
                    elevation={0}
                    defaultExpanded={true}
                    sx={{
                      borderRadius: '16px',
                      backgroundColor: '#FFFFFF',
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap:2,
                      '&:before': {
                        display: 'none',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<IconReusable name='KeyboardArrowDown' width={24} height={24} color='#666' />}
                      aria-controls='tds-content'
                      id='tds-header'
                      sx={{
                        cursor: 'default !important',
                        alignItems: 'flex-start',
                        p: 0,
                        '& .MuiAccordionSummary-content': {
                          margin: 0,
                        },
                        '& .MuiAccordionSummary-content.Mui-expanded': {
                          margin: 0,
                        },
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          alignSelf: 'flex-start',
                          mt: '0px',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <Typography variant='labelM' sx={{ textTransform: 'uppercase', color: '#666666' }}>
                          {selectedData?.tds_summary?.title}
                        </Typography>
                        <Typography
                          variant='h1'
                          sx={{ color: '#CC0000'}}
                        >
                          {selectedData?.tds_summary?.total_amount || '₹ 0'}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      {selectedData?.tds_summary?.breakdown ? (
                        <TDSTable breakdown={selectedData?.tds_summary?.breakdown} />
                      ) : (
                        <Typography>No breakdown data available</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Grid>

              </Grid>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default CumulativeTDS;
